import 'styled-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Text } from '@keyslabor/shared/ui';
import { Logo } from '@/assets';
import { useNavigate } from '@/hooks/useNavigate.ts';
import LayoutPageMobile from '@/components/LayoutPageMobile';
import { useQuestionsAnswers } from '@/hooks/useQuestionsAnswers';
import { Language, StatusOutput } from '@/api/types';
import { routes } from '@/constants.ts';
import { LanguageOptionsList } from '@/components/LanguageOptionsList';
import { getAutoSelectLanguage, normalizeLanguages } from '@/lib/language';

const useAvailableLanguages = (data: StatusOutput | undefined): Language[] => {
  return useMemo(() => {
    if (!data) return [];

    return data.intros.map((intro) => intro.language);
  }, [data]);
};

function Welcome() {
  const { setLanguage, remoteStatus: data } = useQuestionsAnswers();
  const availableLanguages = useAvailableLanguages(data);
  const normalizedLanguages = useMemo(
    () => normalizeLanguages(availableLanguages),
    [availableLanguages]
  );

  const name = data?.name;
  const [params] = useSearchParams();
  const langFromParams = params.get('language') as Language | null;

  const navigate = useNavigate();

  const handleNext = useCallback(
    (language: Language) => {
      setLanguage(language);
      navigate(routes.tutorial);
    },
    [navigate, setLanguage]
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    const autoSelectLanguage = getAutoSelectLanguage({
      availableLanguages: normalizedLanguages,
      languageFromParams: langFromParams,
    });

    if (autoSelectLanguage) {
      handleNext(autoSelectLanguage);
    }
  }, [data, normalizedLanguages, langFromParams, handleNext]);

  return (
    <LayoutPageMobile pb="20px">
      <Row flexCenter>
        <Logo />
        <Text fontWeight="900" mt="30px" fontSize="18px" upper>
          Hello
        </Text>
        <Text fontWeight="900" mt="12px" fontSize="24px" upper>
          {name}!
        </Text>
        <Row flexCenter mt="60px">
          <Text fontWeight="900" fontSize="20px" upper>
            Welcome to
          </Text>
        </Row>
        <Row flexCenter mt="10px">
          <Text fontWeight="800" fontSize="23px" upper>
            {data?.client_name}
          </Text>
        </Row>
        <Text
          fontWeight="800"
          fontSize="18px"
          maxWidth="220px"
          upper
          center
          mt="24px"
        >
          Please select interview language
        </Text>

        <LanguageOptionsList
          languages={normalizedLanguages}
          onSelect={handleNext}
          secondaryMarginTop="40px"
        />
      </Row>
    </LayoutPageMobile>
  );
}

export default Welcome;
